/**
 * JobListing Component
 *
 * Displays a table of available job positions from JobAdder.
 * Supports pagination with 20 results per page.
 *
 * Requirements:
 * - JA-004: Job listing component with filtering
 *
 * @module components/careers/job-listing
 */

import React from 'react';
import PropTypes from 'prop-types';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Link,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  useMultiStyleConfig,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {Link as RouterLink} from 'react-router-dom';

import {formatJobLocation} from './format-job-location';

const jobListingMessages = defineMessages({
  tryAgain: {
    id: 'job_listing.button.try_again',
    defaultMessage: 'Try Again',
  },
  emptyHeading: {
    id: 'job_listing.empty.heading',
    defaultMessage: 'No Current Opportunities',
  },
  emptyBody: {
    id: 'job_listing.empty.body',
    defaultMessage: 'No jobs match your current filters. Try adjusting your selection.',
  },
  colTitle: {
    id: 'job_listing.table.title',
    defaultMessage: 'Title',
  },
  colType: {
    id: 'job_listing.table.type_of_position',
    defaultMessage: 'Type of position',
  },
  colDepartment: {
    id: 'job_listing.table.department',
    defaultMessage: 'Department',
  },
  colLocation: {
    id: 'job_listing.table.location',
    defaultMessage: 'Location',
  },
  viewDetails: {
    id: 'job_listing.link.view_details',
    defaultMessage: 'View Details',
  },
  mobileType: {
    id: 'job_listing.mobile.type_label',
    defaultMessage: 'Type:',
  },
  mobileDepartment: {
    id: 'job_listing.mobile.department_label',
    defaultMessage: 'Department:',
  },
  mobileLocation: {
    id: 'job_listing.mobile.location_label',
    defaultMessage: 'Location:',
  },
  notAvailable: {
    id: 'job_listing.value.not_available',
    defaultMessage: 'N/A',
  },
  paginationPrevious: {
    id: 'job_listing.pagination.previous_page',
    defaultMessage: 'Previous page',
  },
  paginationNext: {
    id: 'job_listing.pagination.next_page',
    defaultMessage: 'Next page',
  },
});

/**
 * JobListing component displaying available positions with pagination
 *
 * @param {Object} props - Component props
 * @param {Array} props.jobs - Array of job listings for current page
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.error - Error message if any
 * @param {number} props.currentPage - Current page number
 * @param {number} props.totalPages - Total number of pages
 * @param {number} props.totalJobs - Total number of jobs
 * @param {Function} props.onPageChange - Callback for page changes
 * @param {React.ReactNode} [props.resultSummary] — "Showing X of Y" (renders left in footer row)
 */
const JobListing = ({
  jobs = [],
  isLoading = false,
  error = null,
  currentPage = 1,
  totalPages = 1,
  totalJobs = 0,
  onJobsRetry,
  onPageChange,
  onJobClick,
  resultSummary = null,
}) => {
  const intl = useIntl();
  const na = intl.formatMessage(jobListingMessages.notAvailable);
  const boardTheme = useMultiStyleConfig('CareersJobBoard', {});
  const isMobile = useBreakpointValue({base: true, md: false});

  if (isLoading) {
    return (
      <Box>
        <Skeleton {...boardTheme.listingSkeletonBanner} />
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} {...boardTheme.listingSkeletonRow} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box {...boardTheme.listingErrorOuter}>
        <Text {...boardTheme.listingErrorText}>{error}</Text>
        <Button
          {...boardTheme.listingErrorRetryBtn}
          onClick={() => (onJobsRetry ? onJobsRetry() : window.location.reload())}
        >
          <FormattedMessage {...jobListingMessages.tryAgain} />
        </Button>
      </Box>
    );
  }

  if (!jobs || jobs.length === 0) {
    return (
      <Box {...boardTheme.listingEmptyOuter}>
        <Heading {...boardTheme.listingEmptyHeading}>
          <FormattedMessage {...jobListingMessages.emptyHeading} />
        </Heading>
        <Text {...boardTheme.listingEmptyText}>
          <FormattedMessage {...jobListingMessages.emptyBody} />
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      {isMobile ? (
        <MobileJobCards boardTheme={boardTheme} jobs={jobs} na={na} onJobClick={onJobClick} />
      ) : (
        <DesktopJobTable boardTheme={boardTheme} jobs={jobs} na={na} onJobClick={onJobClick} />
      )}

      {(resultSummary != null || totalPages > 1) && (
        <Flex
          className="careers-job-listing-footer-row"
          direction="row"
          {...boardTheme.listingFooterRow}
        >
          {totalPages > 1 ? (
            <Box {...boardTheme.listingPaginationWrap}>
              <Pagination
                boardTheme={boardTheme}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
              />
            </Box>
          ) : null}
          {resultSummary ? <Box flex="1" minW={0} {...boardTheme.listingResultSummaryOuter}>{resultSummary}</Box> : null}
        </Flex>
      )}
    </Box>
  );
};

const MobileJobCards = ({jobs, onJobClick, boardTheme, na}) => (
  <VStack {...boardTheme.listingMobileOuter}>
    {jobs.map((job) => (
      <Box key={job.adId || job.id} {...boardTheme.listingMobileRow}>
        <Link
          as={RouterLink}
          {...boardTheme.listingMobileRouterLink}
          to={`/careers/${job.adId || job.id}`}
          onClick={() => onJobClick?.(job)}
        >
          <Text {...boardTheme.listingMobileTitle}>{job.title}</Text>
          <VStack {...boardTheme.listingMobileDetailStack}>
            <Text>
              <Text {...boardTheme.listingMobileDtLabel}>
                <FormattedMessage {...jobListingMessages.mobileType} />
              </Text>{' '}
              {job.workType || job.employmentType || na}
            </Text>
            <Text>
              <Text {...boardTheme.listingMobileDtLabel}>
                <FormattedMessage {...jobListingMessages.mobileDepartment} />
              </Text>{' '}
              {job.category || job.department || na}
            </Text>
            <Text>
              <Text {...boardTheme.listingMobileDtLabel}>
                <FormattedMessage {...jobListingMessages.mobileLocation} />
              </Text>{' '}
              {formatJobLocation(job.location, na)}
            </Text>
          </VStack>
          <Text {...boardTheme.listingMobileFooterLinkText}>
            <FormattedMessage {...jobListingMessages.viewDetails} />
          </Text>
        </Link>
      </Box>
    ))}
  </VStack>
);

const DesktopJobTable = ({jobs, onJobClick, boardTheme, na}) => (
  <Table size="sm" variant="simple" width="full" {...boardTheme.listingTable}>
    <colgroup>
      <col style={{width: '41.09%'}} />
      <col style={{width: '15.5%'}} />
      <col style={{width: '20.5%'}} />
      <col style={{width: '22.98%'}} />
      <col style={{width: '9.44%'}} />
    </colgroup>
    <Thead>
      <Tr {...boardTheme.listingTableDividerRow}>
        <Th {...boardTheme.listingThLeading}>
          <FormattedMessage {...jobListingMessages.colTitle} />
        </Th>
        <Th {...boardTheme.listingThInner}>
          <FormattedMessage {...jobListingMessages.colType} />
        </Th>
        <Th {...boardTheme.listingThInner}>
          <FormattedMessage {...jobListingMessages.colDepartment} />
        </Th>
        <Th {...boardTheme.listingThLocation}>
          <FormattedMessage {...jobListingMessages.colLocation} />
        </Th>
        <Th {...boardTheme.listingThTrailing} />
      </Tr>
    </Thead>
    <Tbody>
      {jobs.map((job) => (
        <Tr key={job.adId || job.id} {...boardTheme.listingTableDividerRow}>
          <Td {...boardTheme.listingTdLeading}>
            <Link
              as={RouterLink}
              {...boardTheme.listingLinkTitleRow}
              to={`/careers/${job.adId || job.id}`}
              onClick={() => onJobClick?.(job)}
            >
              {job.title}
            </Link>
          </Td>
          <Td {...boardTheme.listingTdCell}>{job.workType || job.employmentType || na}</Td>
          <Td {...boardTheme.listingTdCell}>{job.category || job.department || na}</Td>
          <Td {...boardTheme.listingTdLocation}>{formatJobLocation(job.location, na)}</Td>
          <Td {...boardTheme.listingTdAction}>
            <Link
              as={RouterLink}
              {...boardTheme.listingLinkViewDetails}
              to={`/careers/${job.adId || job.id}`}
              onClick={() => onJobClick?.(job)}
            >
              <FormattedMessage {...jobListingMessages.viewDetails} />
            </Link>
          </Td>
        </Tr>
      ))}
    </Tbody>
  </Table>
);

const Pagination = ({boardTheme, currentPage, totalPages, onPageChange}) => {
  const intl = useIntl();
  const iconSize = useBreakpointValue({base: 16, md: 12});
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push('...');
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('...');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <HStack {...boardTheme.listingPaginationStack}>
      {currentPage > 1 ?
        (
          <IconButton
            {...boardTheme.listingPaginationGhostIcon}
            aria-label={intl.formatMessage(jobListingMessages.paginationPrevious)}
            icon={<ChevronLeftIcon size={iconSize} />}
            onClick={() => onPageChange?.(currentPage - 1)}
          />
        ) :
        null}

      {getPageNumbers().map((page, index) =>
        page === '...' ? (
          <Text key={`ellipsis-${index}`} {...boardTheme.listingPaginationEllipsisText}>
            ...
          </Text>
        ) : (
          <Button
            key={page}
            {...(page === currentPage
              ? boardTheme.listingPaginationPageButtonSelected
              : boardTheme.listingPaginationPageButton)}
            onClick={() => onPageChange?.(page)}
          >
            {page}
          </Button>
        )
      )}

      {currentPage < totalPages ?
        (
          <IconButton
            {...boardTheme.listingPaginationGhostIcon}
            aria-label={intl.formatMessage(jobListingMessages.paginationNext)}
            icon={<ChevronRightIcon size={iconSize} />}
            onClick={() => onPageChange?.(currentPage + 1)}
          />
        ) :
        null}
    </HStack>
  );
};

const ChevronLeftIcon = ({size = 20}) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
    <path
      clipRule="evenodd"
      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
      fillRule="evenodd"
    />
  </svg>
);

const ChevronRightIcon = ({size = 20}) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
    <path
      clipRule="evenodd"
      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
      fillRule="evenodd"
    />
  </svg>
);

JobListing.propTypes = {
  jobs: PropTypes.arrayOf(
    PropTypes.shape({
      adId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string.isRequired,
      workType: PropTypes.string,
      employmentType: PropTypes.string,
      category: PropTypes.string,
      department: PropTypes.string,
      location: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          city: PropTypes.string,
          state: PropTypes.string,
          countryCode: PropTypes.string,
        }),
      ]),
    })
  ),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  totalJobs: PropTypes.number,
  onPageChange: PropTypes.func,
  onJobsRetry: PropTypes.func,
  onJobClick: PropTypes.func,
  resultSummary: PropTypes.node,
};

MobileJobCards.propTypes = {
  boardTheme: PropTypes.object.isRequired,
  jobs: PropTypes.array.isRequired,
  na: PropTypes.string.isRequired,
  onJobClick: PropTypes.func,
};

DesktopJobTable.propTypes = {
  boardTheme: PropTypes.object.isRequired,
  jobs: PropTypes.array.isRequired,
  na: PropTypes.string.isRequired,
  onJobClick: PropTypes.func,
};

Pagination.propTypes = {
  boardTheme: PropTypes.object.isRequired,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func,
};

export default JobListing;
